import { useEffect, useState, useMemo, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { LogOut, KeyRound, Search, Menu as MenuIcon } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { roleLabel } from "../lib/format";
import { toast } from "sonner";
import api from "../lib/api";
import { ChangeMyPasswordModal } from "./ChangeMyPasswordModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveLogo = (p) => (p && (p.startsWith("/api/") || p.startsWith("/uploads/"))) ? `${BACKEND}${p}` : (p || "");

const TITLES = {
    "/dashboard": "Dashboard", "/vendor": "Master Vendor", "/jenis-kain": "Master Jenis Kain",
    "/peralatan": "Master Peralatan Pembantu", "/produk": "Master Produk",
    "/users": "Kelola Pengguna", "/account-center": "Account Center",
    "/pengaturan": "Pengaturan Perusahaan", "/spk": "SPK / Job Order", "/bon-kain": "Bon Kain",
    "/bon-peralatan": "Bon Peralatan", "/setor-barang": "Setor Barang Jadi", "/qc": "Pemeriksaan QC",
    "/reject-perbaikan": "Reject Bisa Diperbaiki", "/kasbon": "Kasbon", "/invoice": "Invoice",
    "/laporan": "Laporan / Export", "/konfigurasi-ai": "Konfigurasi AI", "/backup-data": "Backup Data",
};

const JENIS_TONE = {
    SPK: "bg-blue-50 text-blue-700 border-blue-200",
    "Bon Kain": "bg-blue-50 text-blue-700 border-blue-200",
    "Bon Peralatan": "bg-blue-50 text-blue-700 border-blue-200",
    "Setor Barang": "bg-blue-50 text-blue-700 border-blue-200",
    QC: "bg-blue-50 text-blue-700 border-blue-200",
    Reject: "bg-amber-50 text-amber-700 border-amber-200",
    Kasbon: "bg-amber-50 text-amber-700 border-amber-200",
    Invoice: "bg-rose-50 text-rose-700 border-rose-200",
    Vendor: "bg-slate-100 text-slate-700 border-slate-200",
    Produk: "bg-slate-100 text-slate-700 border-slate-200",
};

export const Topbar = ({ onOpenMobileMenu }) => {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const title = useMemo(() => {
        for (const [k, v] of Object.entries(TITLES)) if (loc.pathname.startsWith(k)) return v;
        return "Buku Vendor Garment";
    }, [loc.pathname]);

    // Dynamic breadcrumb: VendorDetail page sets window.__crumb_vendor_name_
    const [crumbName, setCrumbName] = useState("");
    useEffect(() => {
        const sync = () => setCrumbName(window.__crumb_vendor_name_ || "");
        sync();
        const t = setInterval(sync, 500);
        return () => clearInterval(t);
    }, [loc.pathname]);

    const breadcrumbs = useMemo(() => {
        const segs = loc.pathname.split("/").filter(Boolean);
        if (segs[0] === "vendor" && segs[1]) return [
            { label: "Vendor", path: "/vendor" },
            { label: crumbName || "Detail", current: true },
        ];
        if (segs[0] === "spk" && segs[1]) return [{ label: "SPK", path: "/spk" }, { label: "Detail", current: true }];
        if (segs[0] === "invoice" && segs[1]) return [{ label: "Invoice", path: "/invoice" }, { label: segs[1] === "new" ? "Baru" : "Detail", current: true }];
        return [{ label: title, current: true }];
    }, [loc.pathname, title, crumbName]);

    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [logoUrl, setLogoUrl] = useState("");
    const [pwModalOpen, setPwModalOpen] = useState(false);
    const boxRef = useRef(null);

    useEffect(() => {
        api.get("/pengaturan/public").then((r) => setLogoUrl(r.data?.logo_url || "")).catch(() => {});
    }, []);

    useEffect(() => {
        if (!q.trim()) { setResults([]); setOpen(false); return; }
        const t = setTimeout(async () => {
            setLoadingSearch(true);
            try {
                const { data } = await api.get("/search", { params: { q: q.trim() } });
                setResults(data); setOpen(true);
            } catch { /* silent */ }
            finally { setLoadingSearch(false); }
        }, 250);
        return () => clearTimeout(t);
    }, [q]);

    useEffect(() => {
        const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);

    const pick = (r) => { setOpen(false); setQ(""); nav(r.path); };

    const handleLogout = async () => {
        await logout(); toast.success("Berhasil keluar"); nav("/login");
    };

    const initials = (user?.nama || user?.email || "U").trim().charAt(0).toUpperCase();

    return (
        <header
            data-testid="app-topbar"
            className="glass-topbar sticky top-0 z-10 flex items-center justify-between gap-2 md:gap-4 px-4 md:px-6 lg:px-8"
            style={{ height: "var(--topbar-h)" }}
        >
            {/* Left: hamburger (md hidden) + logo + title */}
            <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-shrink">
                <button
                    onClick={onOpenMobileMenu}
                    className="md:hidden p-2 -ml-2 rounded-lg hover:bg-[var(--bg-subtle)]"
                    data-testid="topbar-hamburger"
                    aria-label="Buka menu"
                    style={{ color: "var(--text-primary)" }}
                >
                    <MenuIcon className="w-5 h-5" />
                </button>
                {logoUrl && (
                    <img
                        src={resolveLogo(logoUrl)}
                        alt="logo"
                        className="w-7 h-7 rounded-md object-contain bg-white flex-shrink-0 hidden md:block"
                        style={{ border: "1px solid var(--border-soft)" }}
                        data-testid="topbar-logo"
                        onError={() => setLogoUrl("")}
                    />
                )}
                <h1 className="text-base md:text-lg font-semibold tracking-tight truncate hidden md:block" style={{ color: "var(--text-primary)" }}>
                    <span className="breadcrumb">
                        {breadcrumbs.map((b, i) => (
                            <span key={i} className="flex items-center gap-1.5">
                                {b.current ? (
                                    <span className="current">{b.label}</span>
                                ) : (
                                    <button onClick={() => nav(b.path)} className="hover:underline">{b.label}</button>
                                )}
                                {i < breadcrumbs.length - 1 && <span className="sep">/</span>}
                            </span>
                        ))}
                    </span>
                </h1>
                <h1 className="text-base font-semibold tracking-tight truncate md:hidden" style={{ color: "var(--text-primary)" }}>
                    {title}
                </h1>
            </div>

            {/* Center: search — hide on mobile, show on md+ */}
            <div ref={boxRef} className="relative flex-1 max-w-md hidden md:block">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--text-muted)" }} />
                <Input
                    data-testid="topbar-search-input"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onFocus={() => q && setOpen(true)}
                    placeholder="Cari SPK, invoice, vendor..."
                    className="pl-9 rounded-lg h-9"
                    style={{ background: "#fff", border: "1px solid var(--border)" }}
                />
                {open && (
                    <div
                        data-testid="topbar-search-results"
                        className="absolute top-11 left-0 right-0 max-h-96 overflow-y-auto rounded-xl shadow-lg z-50"
                        style={{ background: "#fff", border: "1px solid var(--border)" }}
                    >
                        {loadingSearch && <div className="p-4 text-xs text-center" style={{ color: "var(--text-muted)" }}>Mencari...</div>}
                        {!loadingSearch && results.length === 0 && (
                            <div className="p-4 text-xs text-center" style={{ color: "var(--text-muted)" }}>
                                Tidak ada hasil untuk "{q}"
                            </div>
                        )}
                        {results.map((r, idx) => (
                            <button
                                key={idx}
                                onClick={() => pick(r)}
                                data-testid={`search-result-${idx}`}
                                className="w-full text-left flex items-center gap-3 px-3 py-2.5 hover:bg-[var(--bg-subtle)] border-b last:border-b-0"
                                style={{ borderColor: "var(--border-soft)" }}
                            >
                                <span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-md border ${JENIS_TONE[r.jenis] || "bg-slate-50 text-slate-700 border-slate-200"} w-24 text-center flex-shrink-0`}>
                                    {r.jenis}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>
                                        {r.nomor || r.extra || "-"}
                                    </div>
                                    {r.extra && r.nomor && (
                                        <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{r.extra}</div>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Right: user menu */}
            <div className="flex items-center gap-2 flex-shrink-0">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            data-testid="topbar-user-menu"
                            className="rounded-lg gap-2 hover:bg-[var(--bg-subtle)] px-2"
                        >
                            <div
                                className="w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm"
                                style={{ background: "var(--apple-blue-soft)", color: "var(--apple-blue)" }}
                            >
                                {initials}
                            </div>
                            <div className="hidden sm:flex flex-col items-start leading-tight">
                                <span className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>
                                    {user?.nama || user?.email}
                                </span>
                                <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                                    {user?.email}
                                </span>
                            </div>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                        <DropdownMenuLabel>
                            <div className="flex flex-col">
                                <span className="font-semibold" style={{ color: "var(--text-primary)" }}>
                                    {user?.nama}
                                </span>
                                <span className="text-xs font-normal" style={{ color: "var(--text-muted)" }}>
                                    {user?.email}
                                </span>
                                <span
                                    className="text-[10px] font-semibold uppercase mt-1 inline-block w-fit px-2 py-0.5 rounded-md"
                                    style={{
                                        background: user?.is_owner ? "var(--apple-blue-soft)" : "var(--bg-subtle)",
                                        color: user?.is_owner ? "var(--apple-blue)" : "var(--text-secondary)",
                                    }}
                                >
                                    {roleLabel(user?.role)}
                                </span>
                            </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            data-testid="topbar-change-password"
                            onClick={() => setPwModalOpen(true)}
                            className="cursor-pointer"
                        >
                            <KeyRound className="w-4 h-4 mr-2" />
                            Ubah Password Saya
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            data-testid="topbar-logout-button"
                            onClick={handleLogout}
                            className="cursor-pointer"
                            style={{ color: "var(--apple-red)" }}
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Keluar
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            <ChangeMyPasswordModal open={pwModalOpen} onOpenChange={setPwModalOpen} />
        </header>
    );
};
